<template>
  <nav 
    :class="[dividerClass, 'border-t']" 
    role="navigation"
    :aria-label="ariaLabel"
  >
    <Text
      v-if="title"
      as="p"
      variant="body-sm"
      weight="medium"
      :class="`${titleColor} mb-3`"
      id="quick-links-heading"
    >
      {{ title }}
    </Text>
    <div 
      class="flex flex-wrap justify-center gap-x-6 gap-y-3"
      role="list"
      :aria-labelledby="title ? 'quick-links-heading' : undefined"
    >
      <router-link
        v-for="link in links"
        :key="link.to"
        :to="link.to"
        :class="linkColor"
        class="inline-flex items-center text-sm font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded px-2 py-1 transition-colors"
        role="listitem"
      >
        <svg 
          v-if="link.icon" 
          class="w-4 h-4 mr-2" 
          fill="none" 
          stroke="currentColor" 
          aria-hidden="true"
          v-bind="getIconAttributes()"
        >
          <path :d="getIconPath(link.icon)" />
        </svg>
        <Text as="span" variant="body-sm" :class="'font-medium'">{{ link.label }}</Text>
      </router-link>
    </div>
  </nav>
</template>

<script setup>
import Text from './Text.vue'
import { getIconPath, getIconAttributes } from '@/constants/icons'

/**
 * Props
 */
defineProps({
  /**
   * Title/suggestion text to display above links
   */
  title: {
    type: String,
    default: ''
  },
  
  /**
   * Array of link objects with { to, label, icon }
   */
  links: {
    type: Array,
    required: true,
    validator: (links) => {
      return links.every(link => 
        link.to && link.label
      )
    }
  },
  
  /**
   * CSS classes for the divider section
   */
  dividerClass: {
    type: String,
    default: 'mt-8 pt-5 border-gray-200 dark:border-gray-700'
  },
  
  /**
   * CSS classes for the title text color
   */
  titleColor: {
    type: String,
    default: 'text-gray-500 dark:text-gray-400'
  },
  
  /**
   * CSS classes for the link text color
   */
  linkColor: {
    type: String,
    default: 'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
  },
  
  /**
   * ARIA label for the navigation
   */
  ariaLabel: {
    type: String,
    required: true
  }
})
</script>

